/* =====================================================
   SIFI BEAUVOIS — RÉTRACTATION EN LIGNE
   Parcours de la page « Se rétracter ici » (section sifi-retractation).

   ① déclaration → ② récapitulatif → ③ accusé, sans rechargement.

   À la confirmation, DEUX envois lancés EN PARALLÈLE :
     • événement Klaviyo « Retour demandé » → déclenche le flow qui
       envoie l'accusé de réception horodaté au client ;
     • POST /contact (formulaire natif Shopify) → notification interne.

   ⚠️ AUCUN champ « time » n'est envoyé à Klaviyo — volontaire, et c'est le
   point le plus important du fichier. L'horloge du navigateur d'un client
   n'est pas une preuve : c'est Klaviyo qui doit estampiller côté serveur.
   La date affichée à l'écran (panneau ③) est purement informative, forcée sur
   Europe/Paris pour rester cohérente avec le fuseau du compte Klaviyo.

   ⚠️ Repli : si un envoi échoue, on n'annule PAS le parcours. Le client a
   exercé son droit ; on lui affiche le panneau ③ avec un bandeau et un lien
   mailto pré-rempli vers le service client. Ne jamais bloquer quelqu'un dans
   l'exercice d'un droit sur un échec technique.

   Style ES5 volontaire : même registre que contact-page.js et footer.js.
   ===================================================== */

(function () {
  'use strict';

  var KLAVIYO_ENDPOINT = 'https://a.klaviyo.com/client/events/';
  var CONTACT_ENDPOINT = '/contact';

  /* Locale d'affichage des dates : le layout pose lang sur <html>.
     Le FUSEAU reste Europe/Paris (coherence avec l'horodatage Klaviyo). */
  var LOC = (document.documentElement && document.documentElement.lang) || 'fr';

  /* Code de langue COURT pour Klaviyo ('fr', 'en'), pose en propriete de
     l'evenement : c'est lui que lit le split conditionnel du flow, qui
     compare une chaine exacte. On coupe au tiret car <html lang> peut valoir
     'en-CH' ou 'en-US' selon les sous-dossiers de marche — sans cette coupe,
     un client suisse anglophone tomberait dans la branche par defaut.
     ⚠️ La branche par defaut du split DOIT etre le francais : un evenement
     sans propriete (ancien, ou echec de lecture) part alors en francais
     plutot que nulle part. */
  var LANGUE = String(LOC).toLowerCase().split('-')[0] || 'fr';

  /* ---------- utilitaires ---------- */

  function txt(el) {
    return el && el.value ? String(el.value).trim() : '';
  }

  /* Numéro de commande normalisé. Une seule forme sert l'écran de vérification,
     l'e-mail du client et la notification interne.

     ⚠️ CORRIGÉ LE 05/08/2026. La version d'origine ajoutait un « # » en tête,
     parce que les commandes s'appelaient alors « #1024 ». Le préfixe de la
     boutique est désormais « SB » : un client saisissant SB1024 se voyait
     renvoyer « #SB1024 » — un dièse parasite sur une référence qui n'en a pas,
     y compris dans l'e-mail que le client conserve. On ne préfixe donc plus
     rien : on nettoie ce qui est saisi, sans rien inventer.

     Ce qui est nettoyé : espaces de tête et de fin, dièse de tête (réflexe
     fréquent, ou copie depuis une autre boutique), espaces internes multiples,
     et passage en capitales — les références sont alphanumériques, « sb1024 »
     et « SB1024 » désignent la même commande.

     Volontairement NON fait : compléter « 1024 » en « SB1024 ». Ce serait
     commode, mais les commandes antérieures au changement de préfixe ne
     portent pas « SB » — on inventerait une référence inexistante. Mieux vaut
     transmettre ce que le client a écrit. */
  function orderRef(v) {
    return String(v || '')
      .replace(/^[#\s]+/, '')
      .replace(/\s+/g, ' ')
      .trim()
      .toUpperCase();
  }

  function isEmail(v) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v);
  }

  function frDate(d) {
    try {
      return d.toLocaleDateString(LOC, {
        timeZone: 'Europe/Paris', day: '2-digit', month: 'long', year: 'numeric'
      });
    } catch (e) {
      return d.toLocaleDateString(LOC);
    }
  }

  function frTime(d) {
    try {
      return d.toLocaleTimeString(LOC, {
        timeZone: 'Europe/Paris', hour: '2-digit', minute: '2-digit'
      });
    } catch (e) {
      return d.toLocaleTimeString(LOC);
    }
  }

  /* Date/heure prêtes à afficher, forcées sur Europe/Paris. Envoyées comme
     PROPRIÉTÉS de l'événement (pas comme champ `time`) : elles servent
     uniquement au rendu de l'e-mail Klaviyo, dont le sélecteur de variables
     n'expose aucun horodatage utilisable (constaté le 31/07).
     ⚠️ L'horodatage de PREUVE reste celui posé par le serveur Klaviyo à la
     réception de l'événement — inchangé, et toujours pas envoyé par nous.
     Ces deux chaînes sont cosmétiques ; elles ont l'avantage d'afficher dans
     l'e-mail exactement l'heure que le client a vue à l'écran. */
  function frDateShort(d) {
    try {
      return d.toLocaleDateString(LOC, {
        timeZone: 'Europe/Paris', day: '2-digit', month: '2-digit', year: 'numeric'
      });
    } catch (e) {
      return d.toLocaleDateString(LOC);
    }
  }

  /* ---------- module ---------- */

  function initRetractation(root) {
    var scope = root || document;
    var page = scope.querySelector('[data-sb-retract]');
    if (!page || page.dataset.sbRetractInit === '1') return;
    page.dataset.sbRetractInit = '1';

    var form = page.querySelector('[data-sb-retract-form]');
    var panels = {
      form:   page.querySelector('[data-sb-panel="form"]'),
      review: page.querySelector('[data-sb-panel="review"]'),
      done:   page.querySelector('[data-sb-panel="done"]')
    };
    if (!form || !panels.form || !panels.review || !panels.done) return;

    var errorsBox    = page.querySelector('[data-sb-errors]');
    var confirmError = page.querySelector('[data-sb-confirm-error]');
    var summaryBox   = page.querySelector('[data-sb-summary]');
    var confirmBtn   = page.querySelector('[data-sb-confirm]');
    var backBtn      = page.querySelector('[data-sb-back]');
    var fallbackBox  = page.querySelector('[data-sb-fallback]');
    var mailtoLink   = page.querySelector('[data-sb-mailto]');

    var klaviyoKey      = page.getAttribute('data-klaviyo-key') || '';
    var klaviyoMetric   = page.getAttribute('data-klaviyo-metric') || 'Retour demandé';
    var klaviyoRevision = page.getAttribute('data-klaviyo-revision') || '2026-04-15';
    var contactEmail    = page.getAttribute('data-contact-email') || 'contact@sifibeauvois.com';

    /* Libelles poses par le Liquid (data-txt-*), replis FR. */
    function attrTxt(nom, repli) { return page.getAttribute(nom) || repli; }
    var TXT_REQ_FIRSTNAME = attrTxt('data-txt-req-firstname', 'Merci d’indiquer votre prénom.');
    var TXT_REQ_LASTNAME = attrTxt('data-txt-req-lastname', 'Merci d’indiquer votre nom.');
    var TXT_REQ_EMAIL = attrTxt('data-txt-req-email', 'Merci d’indiquer votre adresse e-mail.');
    var TXT_REQ_ORDER = attrTxt('data-txt-req-order', 'Merci d’indiquer votre numéro de commande.');
    var TXT_EMAIL_INVALID = attrTxt('data-txt-email-invalid', 'Cette adresse e-mail ne semble pas valide.');
    var TXT_SUM_NAME = attrTxt('data-txt-sum-name', 'Nom et prénom');
    var TXT_LABEL_EMAIL = attrTxt('data-txt-label-email', 'Adresse e-mail');
    var TXT_LABEL_ORDER = attrTxt('data-txt-label-order', 'Numéro de commande');
    var TXT_LABEL_PRODUCT = attrTxt('data-txt-label-product', 'Produit concerné');
    var TXT_LABEL_REASON = attrTxt('data-txt-label-reason', 'Motif');
    var TXT_COLON = attrTxt('data-txt-colon', ' :');
    var TXT_PLAIN_TITLE = attrTxt('data-txt-plain-title', 'DEMANDE DE RETOUR (exercice du droit de rétractation)');
    var TXT_PLAIN_FOOTER = attrTxt('data-txt-plain-footer', 'Demande transmise depuis la page « Retour et remboursement » du site.');
    var TXT_MAILTO_SUBJECT = attrTxt('data-txt-mailto-subject', 'Demande de retour — commande');

    var fields = {
      firstname: form.querySelector('[name="firstname"]'),
      lastname:  form.querySelector('[name="lastname"]'),
      email:     form.querySelector('[name="email"]'),
      order:     form.querySelector('[name="order"]'),
      product:   form.querySelector('[name="product"]'),
      reason:    form.querySelector('[name="reason"]')
    };

    var data = null;

    /* ---------- navigation entre panneaux ---------- */

    function show(name) {
      Object.keys(panels).forEach(function (key) {
        var p = panels[key];
        if (key === name) {
          p.hidden = false;
          /* forcer un reflow pour que la transition rejoue à chaque affichage */
          void p.offsetWidth;
          p.classList.add('is-active');
        } else {
          p.classList.remove('is-active');
          p.hidden = true;
        }
      });

      var top = page.getBoundingClientRect().top + window.pageYOffset - 110;
      try {
        window.scrollTo({ top: top > 0 ? top : 0, behavior: 'smooth' });
      } catch (e) {
        window.scrollTo(0, top > 0 ? top : 0);
      }
    }

    /* ---------- validation ---------- */

    function showErrors(box, messages) {
      if (!box) return;
      if (!messages.length) { box.hidden = true; box.innerHTML = ''; return; }
      box.innerHTML = messages.join('<br>');
      box.hidden = false;
    }

    function collect() {
      var messages = [];
      var required = [
        [fields.firstname, TXT_REQ_FIRSTNAME],
        [fields.lastname,  TXT_REQ_LASTNAME],
        [fields.email,     TXT_REQ_EMAIL],
        [fields.order,     TXT_REQ_ORDER]
      ];

      required.forEach(function (pair) {
        var el = pair[0];
        if (!el) return;
        el.classList.remove('is-invalid');
        if (!txt(el)) {
          el.classList.add('is-invalid');
          messages.push(pair[1]);
        }
      });

      if (fields.email && txt(fields.email) && !isEmail(txt(fields.email))) {
        fields.email.classList.add('is-invalid');
        messages.push(TXT_EMAIL_INVALID);
      }

      if (messages.length) {
        showErrors(errorsBox, messages);
        var firstBad = form.querySelector('.is-invalid');
        if (firstBad) firstBad.focus();
        return null;
      }

      showErrors(errorsBox, []);

      return {
        firstname: txt(fields.firstname),
        lastname:  txt(fields.lastname),
        email:     txt(fields.email),
        order:     orderRef(txt(fields.order)),
        product:   txt(fields.product),
        reason:    txt(fields.reason)
      };
    }

    /* ---------- récapitulatif ---------- */

    function rows(d) {
      var out = [
        [TXT_SUM_NAME, d.firstname + ' ' + d.lastname],
        [TXT_LABEL_EMAIL, d.email],
        [TXT_LABEL_ORDER, d.order]
      ];
      if (d.product) out.push([TXT_LABEL_PRODUCT, d.product]);
      if (d.reason)  out.push([TXT_LABEL_REASON, d.reason]);
      return out;
    }

    function renderSummary(d) {
      if (!summaryBox) return;
      summaryBox.innerHTML = '';
      rows(d).forEach(function (row) {
        var wrap = document.createElement('div');
        wrap.className = 'sb-retract-summary__row';

        var dt = document.createElement('dt');
        dt.className = 'sb-retract-summary__label';
        dt.textContent = row[0];

        var dd = document.createElement('dd');
        dd.className = 'sb-retract-summary__value';
        dd.textContent = row[1];

        wrap.appendChild(dt);
        wrap.appendChild(dd);
        summaryBox.appendChild(wrap);
      });
    }

    /* Texte lisible, réutilisé pour la notification interne ET le mailto de repli. */
    function plainText(d) {
      /* Registre commercial « retour » côté client, qualification juridique entre
         parenthèses : ce texte sert À LA FOIS au mailto du client et à la
         notification interne — Naji doit y lire tout de suite de quoi il s'agit. */
      var lines = [TXT_PLAIN_TITLE, ''];
      rows(d).forEach(function (row) { lines.push(row[0] + TXT_COLON + ' ' + row[1]); });
      lines.push('');
      lines.push(TXT_PLAIN_FOOTER);
      return lines.join('\n');
    }

    /* ---------- envoi 1 : événement Klaviyo ---------- */

    function klaviyoBody(d, minimalProfile, now) {
      var profileAttributes = { email: d.email };
      if (!minimalProfile) {
        profileAttributes.first_name = d.firstname;
        profileAttributes.last_name = d.lastname;
      }

      return {
        data: {
          type: 'event',
          attributes: {
            /* properties est REQUIS par l'API, même vide. On y met tout :
               c'est la source des variables du modèle d'e-mail Klaviyo. */
            properties: {
              prenom: d.firstname,
              nom: d.lastname,
              date_demande: frDateShort(now),
              heure_demande: frTime(now),
              numero_commande: d.order,
              produit: d.product || '',
              motif: d.reason || '',
              langue: LANGUE,
              recapitulatif: plainText(d)
            },
            metric: {
              data: { type: 'metric', attributes: { name: klaviyoMetric } }
            },
            profile: {
              data: { type: 'profile', attributes: profileAttributes }
            }
            /* PAS de "time" : horodatage serveur Klaviyo (cf. en-tête). */
          }
        }
      };
    }

    function postKlaviyo(d, minimalProfile, now) {
      if (!klaviyoKey) return Promise.reject(new Error('Clé Klaviyo absente'));

      return fetch(KLAVIYO_ENDPOINT + '?company_id=' + encodeURIComponent(klaviyoKey), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'revision': klaviyoRevision },
        body: JSON.stringify(klaviyoBody(d, minimalProfile, now))
      }).then(function (res) {
        if (res.status === 202) return true;

        /* 400 = payload refusé. Le cas le plus probable est le profil enrichi
           (first_name / last_name) : on retente une fois avec l'e-mail seul
           plutôt que de perdre l'accusé de réception. */
        if (res.status === 400 && !minimalProfile) return postKlaviyo(d, true, now);

        throw new Error('Klaviyo ' + res.status);
      });
    }

    /* ---------- envoi 2 : notification interne via /contact ---------- */

    /* Le POST en `fetch` vers /contact est REFUSÉ par Shopify (constaté au premier
       test réel, 31/07 : Klaviyo passait, la notification interne n'arrivait
       jamais). Shopify n'accepte ce formulaire qu'en soumission de formulaire
       classique. On soumet donc un vrai <form> vers une <iframe> cachée : le
       navigateur fait une navigation authentique — que Shopify accepte — mais
       c'est l'iframe qui la reçoit, donc la page ne bouge pas et le parcours en
       trois panneaux est préservé.
       Le `load` de l'iframe sert d'accusé de bonne fin ; il se déclenche aussi
       à l'insertion (about:blank), d'où le drapeau `submitted`. Filet à 10 s.
       ⚠️ NE PAS revenir au fetch : ce n'est pas un réglage à trouver, Shopify
       refuse la requête elle-même. */
    function postContact(d) {
      return new Promise(function (resolve, reject) {
        var frameName = 'sb-retract-sink-' + String(Math.floor(performance.now()));
        var submitted = false;
        var settled = false;

        var frame = document.createElement('iframe');
        frame.name = frameName;
        frame.setAttribute('aria-hidden', 'true');
        frame.setAttribute('tabindex', '-1');
        frame.style.cssText = 'position:absolute;width:0;height:0;border:0;opacity:0;pointer-events:none;';

        var hidden = document.createElement('form');
        hidden.method = 'POST';
        hidden.action = CONTACT_ENDPOINT;
        hidden.target = frameName;
        hidden.setAttribute('accept-charset', 'UTF-8');
        hidden.style.display = 'none';

        [
          ['form_type', 'contact'],
          ['utf8', '✓'],
          ['contact[name]', d.firstname + ' ' + d.lastname],
          ['contact[email]', d.email],
          ['contact[body]', plainText(d)]
        ].forEach(function (pair) {
          var input = document.createElement('input');
          input.type = 'hidden';
          input.name = pair[0];
          input.value = pair[1];
          hidden.appendChild(input);
        });

        function cleanup() {
          window.setTimeout(function () {
            if (hidden.parentNode) hidden.parentNode.removeChild(hidden);
            if (frame.parentNode) frame.parentNode.removeChild(frame);
          }, 0);
        }

        var timer = window.setTimeout(function () {
          if (settled) return;
          settled = true;
          cleanup();
          reject(new Error('Shopify /contact : délai dépassé'));
        }, 10000);

        frame.addEventListener('load', function () {
          if (!submitted || settled) return;
          settled = true;
          window.clearTimeout(timer);
          cleanup();
          resolve(true);
        });

        document.body.appendChild(frame);
        document.body.appendChild(hidden);

        submitted = true;
        hidden.submit();
      });
    }

    /* ---------- panneau ③ ---------- */

    function finish(d, allSent, now) {

      var dateEl  = page.querySelector('[data-sb-date]');
      var timeEl  = page.querySelector('[data-sb-time]');
      var emailEl = page.querySelector('[data-sb-sent-email]');
      if (dateEl)  dateEl.textContent = frDate(now);
      if (timeEl)  timeEl.textContent = frTime(now);
      if (emailEl) emailEl.textContent = d.email;

      if (!allSent && fallbackBox) {
        if (mailtoLink) {
          mailtoLink.setAttribute('href',
            'mailto:' + contactEmail +
            '?subject=' + encodeURIComponent(TXT_MAILTO_SUBJECT + ' ' + d.order) +
            '&body=' + encodeURIComponent(plainText(d)));
        }
        fallbackBox.hidden = false;
      }

      show('done');
    }

    /* ---------- écouteurs ---------- */

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var collected = collect();
      if (!collected) return;
      data = collected;
      renderSummary(data);
      showErrors(confirmError, []);
      show('review');
    });

    if (backBtn) {
      backBtn.addEventListener('click', function () { show('form'); });
    }

    if (confirmBtn) {
      confirmBtn.addEventListener('click', function () {
        if (!data) { show('form'); return; }

        confirmBtn.disabled = true;
        showErrors(confirmError, []);

        /* Une seule lecture de l'horloge : l'écran et l'e-mail doivent afficher
           la même heure, à la seconde près. */
        var stamp = new Date();

        var jobs = [
          postKlaviyo(data, false, stamp).then(function () { return true; }, function () { return false; }),
          postContact(data).then(function () { return true; }, function () { return false; })
        ];

        Promise.all(jobs).then(function (results) {
          confirmBtn.disabled = false;
          finish(data, results[0] === true && results[1] === true, stamp);
        });
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initRetractation(document); });
  } else {
    initRetractation(document);
  }

  /* Éditeur de thème : la section peut être rechargée à chaud. */
  document.addEventListener('shopify:section:load', function (e) {
    var target = e && e.target ? e.target : document;
    var page = target.querySelector ? target.querySelector('[data-sb-retract]') : null;
    if (page) { page.dataset.sbRetractInit = ''; initRetractation(target); }
  });
})();